import { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useCallback, useRef } from 'react';

const SHOW_THRESHOLD = 50;
const ANIMATION_DURATION = 200;
const SCROLL_DEBOUNCE = 1;
const MIN_SCROLLABLE_OVERFLOW_TO_COLLAPSE = 150;

interface UseHideOnScrollAnimationProps {
  isEnabled: boolean;
  onCollapse?: () => void;
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
}

export const useHideOnScrollAnimation = ({
  isEnabled,
  onScroll,
  onCollapse,
}: UseHideOnScrollAnimationProps) => {
  const orderOptionsOpacity = useSharedValue(1);
  const lastScrollY = useRef(0);
  const wasVisible = useRef(true);

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      onScroll?.(event);

      if (!isEnabled) {
        return;
      }

      const { contentOffset, contentSize, layoutMeasurement } =
        event.nativeEvent;

      if (
        contentSize.height - layoutMeasurement.height <
        MIN_SCROLLABLE_OVERFLOW_TO_COLLAPSE
      ) {
        if (orderOptionsOpacity.value < 0.9) {
          orderOptionsOpacity.value = withTiming(1, {
            duration: ANIMATION_DURATION,
          });
          wasVisible.current = true;
        }
        lastScrollY.current = contentOffset.y;
        return;
      }

      const currentScrollY = contentOffset.y;
      const scrollDifference = Math.abs(currentScrollY - lastScrollY.current);

      if (scrollDifference > SCROLL_DEBOUNCE) {
        const shouldShow = currentScrollY <= SHOW_THRESHOLD;
        const currentOpacity = orderOptionsOpacity.value;

        if (shouldShow && currentOpacity < 0.9) {
          orderOptionsOpacity.value = withTiming(1, {
            duration: ANIMATION_DURATION,
          });
          wasVisible.current = true;
        } else if (!shouldShow && currentOpacity > 0.1) {
          orderOptionsOpacity.value = withTiming(0, {
            duration: ANIMATION_DURATION,
          });

          // Call onCollapse when transitioning from visible to hidden
          if (wasVisible.current && onCollapse) {
            onCollapse();
          }
          wasVisible.current = false;
        }

        lastScrollY.current = currentScrollY;
      }
    },
    [isEnabled, onScroll, onCollapse, orderOptionsOpacity],
  );

  const animatedStyle = useAnimatedStyle(() => {
    const isVisible = orderOptionsOpacity.value > 0.5;

    return {
      maxHeight: withTiming(isVisible ? 200 : 0, {
        duration: ANIMATION_DURATION,
      }),
      opacity: orderOptionsOpacity.value,
      overflow: 'hidden',
      transform: [
        {
          translateY: withTiming(isVisible ? 0 : -10, {
            duration: ANIMATION_DURATION,
          }),
        },
        {
          scale: withTiming(isVisible ? 1 : 0.95, {
            duration: ANIMATION_DURATION,
          }),
        },
      ],
    };
  });

  return {
    animatedStyle,
    handleScroll,
  };
};
